/**
 * Turning a report-download lead into a deal.
 *
 * ⚠️ **THE PROPOSAL FLOW WITH THE SERVICES TAKEN OUT, AND NOTHING ELSE
 * CHANGED.** `ProposalConvertFlow.tsx` carries the reasoning for every step at
 * length; this file reuses its company chooser ({@link CompanyChoices}) and its
 * report renderer ({@link ReportLine}) rather than copying them, so the two
 * flows cannot come to disagree about how a match is offered or how a partial
 * outcome is worded.
 *
 * The owner's decisions, in order:
 *
 *  1. **Show the companies whose name looks like the lead's `organisation`, and
 *     NEVER auto-select** — not even on an exact match. School-level names share
 *     long prefixes by design, and a wrong merge is the unrecoverable failure.
 *     *"None of these — create a new company"* is a first-class row.
 *  2. **A created company is `pending`**, and the deal is offered at once with
 *     `allowCompanyAwaitingApproval` — the exemption the proposal flow carries,
 *     for the identical reason and with the identical accepted cost.
 *  3. **The deal opens with the company and nothing else.** A report download
 *     names no service, so there is no line to seed — ⛔ and none may be
 *     invented from the report's title.
 *  4. **The deal is recorded on the lead afterwards**, which blocks a second
 *     conversion.
 *  5. **A lead already linked to a company skips step 1** and raises the deal
 *     against that company. Converting does not archive the lead.
 *
 * ## ⛔ NOTHING HERE CLAIMS A ROLLBACK
 *
 * Company, deal and link are three writes with no transaction across them. The
 * report says what each one did, step by step.
 *
 * ⚠️ **MOUNT ONCE, AT THE PAGE — NEVER PER ROW.** It mounts `CompanyFormModal`
 * and `DealFormModal`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { CompanyFormModal } from '@/components/companies/CompanyFormModal';
import { DealFormModal } from '@/components/deals/DealFormModal';
import { Button, Modal, Skeleton } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { composeFullName } from '@/lib/contact-name';
import type { Company, Deal, ReportDownloadLead, SimilarCompany } from '@/lib/types';

import {
  CompanyChoices,
  CREATE_NEW,
  ReportLine,
  type ProposalDealLinkOutcome,
} from './ProposalConvertFlow';

/* -------------------------------------------------------------------------- */
/* Whether this lead may be converted at all                                  */
/* -------------------------------------------------------------------------- */

/**
 * The gate — **the PAIR, never `dealId` alone**, mirroring
 * `proposalIsConvertible` exactly and for the same reason: `deal_id` is
 * `ON DELETE SET NULL`, so a deleted deal leaves it byte-identical to a lead
 * nobody has converted, and `convertedAt` is the stamp that outlives it.
 *
 * ⛔ **`typeof`, never `!== null`** — each field is optional because this app
 * can deploy ahead of its API, and an absent key reads `undefined`.
 *
 * ⚠️ **Exported so there is ONE copy** — the panel's row model and this flow's
 * page-level re-check both read it.
 */
export function reportLeadIsConvertible(lead: ReportDownloadLead): boolean {
  return typeof lead.dealId !== 'number' && typeof lead.convertedAt !== 'string';
}

/**
 * The whole name, joined without inventing a missing half — the same first +
 * last rule every contact form uses for Full name (lib/contact-name.ts).
 */
export function reportLeadPersonName(lead: Pick<ReportDownloadLead, 'firstName' | 'lastName'>): string {
  return composeFullName(lead.firstName, typeof lead.lastName === 'string' ? lead.lastName : null);
}

/* -------------------------------------------------------------------------- */
/* What happened, step by step                                                */
/* -------------------------------------------------------------------------- */

type CompanyStep =
  | { kind: 'chosen'; company: { id: number; name: string } }
  /* Already on the lead — the chooser is skipped. */
  | { kind: 'linked'; company: { id: number; name: string } }
  | { kind: 'created'; company: Company };

type DealStep =
  | { kind: 'none' }
  | {
      kind: 'created';
      deal: Deal;
      linked: 'yes' | 'unconfirmed' | 'failed';
      linkMessage: string | null;
    };

interface ConvertReport {
  company: CompanyStep;
  deal: DealStep;
}

/**
 * The server's own sentence for a refused link — read, never composed, and
 * ordered by specificity (the field error, then the form message, then
 * `message`). ⛔ Neither refusal code is branched on here; the server's
 * sentence already says which case a reader is in.
 */
function linkRefusalMessage(caught: unknown): string | null {
  if (!(caught instanceof ApiError)) return null;
  const onField = caught.fieldErrors['dealId'];
  if (typeof onField === 'string' && onField.trim() !== '') return onField;
  const form = caught.formMessage;
  if (typeof form === 'string' && form.trim() !== '') return form;
  return caught.message.trim() === '' ? null : caught.message;
}

type Stage =
  | { name: 'looking' }
  | { name: 'choosing' }
  | { name: 'creating' }
  | { name: 'deal'; company: CompanyStep }
  | { name: 'linking'; company: CompanyStep; deal: Deal }
  | { name: 'report'; report: ConvertReport };

/** Held apart from {@link Stage} so a trip into the create form and back keeps it. */
interface Lookup {
  matches: SimilarCompany[];
  failed: boolean;
  noName: boolean;
}

const SIMILAR_LIMIT = 5;

export interface ReportDownloadConvertFlowProps {
  open: boolean;
  lead: ReportDownloadLead;
  onClose: () => void;
  /**
   * Record the deal on the lead. **REQUIRED**, and it must REJECT on failure:
   * a resolved promise is read as *the link landed*. ⛔ **No `AbortSignal`** —
   * the deal already exists by the time this runs, so the write must finish.
   */
  onRecordDeal: (dealId: number) => Promise<ProposalDealLinkOutcome>;
}

export function ReportDownloadConvertFlow({
  open,
  lead,
  onClose,
  onRecordDeal,
}: ReportDownloadConvertFlowProps) {
  const [stage, setStage] = useState<Stage>({ name: 'looking' });
  const [lookup, setLookup] = useState<Lookup>({ matches: [], failed: false, noName: false });
  const [choice, setChoice] = useState<string>('');
  /* Mounted flag for the link write, which is deliberately not aborted. */
  const mounted = useRef(true);

  const organisation = lead.organisation.trim();
  /* A lead already linked to a company raises the deal against it — no chooser. */
  const linkedCompany = lead.company ?? null;
  const linkedId = linkedCompany?.id ?? null;
  const linkedName = linkedCompany?.name ?? null;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const { signal } = controller;

    setStage({ name: 'looking' });
    setLookup({ matches: [], failed: false, noName: false });
    setChoice('');

    if (linkedId !== null) {
      setStage({
        name: 'deal',
        company: { kind: 'linked', company: { id: linkedId, name: linkedName ?? '' } },
      });
      return () => controller.abort();
    }

    /*
     * A blank organisation cannot be looked up — an empty `?name=` is a 422 on
     * a `.strict()` schema, not "no filter" — so it is not asked, and that third
     * state is reported as itself.
     */
    const found: Promise<{ matches: SimilarCompany[]; failed: boolean }> = organisation
      ? api
          .listSimilarCompanies({ name: organisation, limit: SIMILAR_LIMIT }, signal)
          .then((answer) => ({ matches: answer.data, failed: false }))
          .catch(() => ({ matches: [] as SimilarCompany[], failed: true }))
      : Promise.resolve({ matches: [] as SimilarCompany[], failed: false });

    void found.then((result) => {
      if (signal.aborted) return;
      setLookup({ matches: result.matches, failed: result.failed, noName: organisation === '' });
      setStage({ name: 'choosing' });
    });

    return () => controller.abort();
  }, [open, organisation, lead.id, linkedId, linkedName]);

  const dealSaved = useCallback(
    (deal: Deal, company: CompanyStep) => {
      setStage({ name: 'linking', company, deal });
      void (async () => {
        let linked: 'yes' | 'unconfirmed' | 'failed' = 'yes';
        let linkMessage: string | null = null;
        try {
          linked = (await onRecordDeal(deal.id)) === 'recorded' ? 'yes' : 'unconfirmed';
        } catch (caught) {
          /* NOT aborted on unmount: the deal exists, and the link must finish. */
          linked = 'failed';
          linkMessage = linkRefusalMessage(caught);
        }
        if (!mounted.current) return;
        setStage({
          name: 'report',
          report: { company, deal: { kind: 'created', deal, linked, linkMessage } },
        });
      })();
    },
    [onRecordDeal],
  );

  if (!open) return null;

  /* --- creating a company ------------------------------------------------- */

  if (stage.name === 'creating') {
    /*
     * Closing the create form BEFORE a save is a step BACK — nothing was
     * created. After a save the form shows its approval confirmation and
     * `onSaved` fires on that window's button or close, possibly followed by
     * `onClose` in the same tick: the functional update keeps a close that
     * follows the save from undoing the move to the deal stage.
     */
    return (
      <CompanyFormModal
        open
        createdContinueLabel="Continue to deal"
        onClose={() => setStage((prev) => (prev.name === 'creating' ? { name: 'choosing' } : prev))}
        onSaved={(company) => setStage({ name: 'deal', company: { kind: 'created', company } })}
      />
    );
  }

  /* --- the deal ----------------------------------------------------------- */

  if (stage.name === 'deal') {
    const company =
      stage.company.kind === 'created'
        ? { id: stage.company.company.id, name: stage.company.company.name }
        : stage.company.company;

    return (
      <DealFormModal
        key={`report-lead:${lead.id}:${company.id}`}
        open
        onClose={() => {
          /* Nothing was written on the linked path, so there is nothing to report. */
          if (stage.company.kind === 'linked') {
            onClose();
            return;
          }
          setStage({ name: 'report', report: { company: stage.company, deal: { kind: 'none' } } });
        }}
        lockedCompany={company}
        /*
         * The same owner exemption the proposal flow passes, unconditionally,
         * for the same reason: a company created seconds ago is `pending` by
         * construction. ⚠️ A REJECTED company is still refused — the flag tests
         * `pending` alone. The accepted cost (a live deal naming a company that
         * is later rejected, with nothing cleaning it up) is recorded at the
         * proposal flow's call site.
         */
        allowCompanyAwaitingApproval
        onSaved={(deal) => dealSaved(deal, stage.company)}
      />
    );
  }

  /* --- recording the deal on the lead ------------------------------------- */

  if (stage.name === 'linking') {
    return (
      <Modal open onClose={() => {}} title="Recording the deal" size="sm">
        <p className="text-xs text-fg-secondary">
          Deal #{stage.deal.id} was created. Recording it on this lead…
        </p>
        {/* A consequence: the × above will not stop this, and must not. */}
        <p className="mt-1 text-2xs text-fg-muted">
          This step can’t be interrupted. It finishes on its own.
        </p>
        <Skeleton className="mt-2 h-4 w-40" />
      </Modal>
    );
  }

  /* --- the report --------------------------------------------------------- */

  if (stage.name === 'report') {
    return (
      <Modal
        open
        onClose={onClose}
        title="What happened"
        size="md"
        footer={
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        }
      >
        <ReportBody report={stage.report} />
      </Modal>
    );
  }

  /* --- looking, and choosing ---------------------------------------------- */

  const looking = stage.name === 'looking';
  const canContinue = !looking && choice !== '';

  return (
    <Modal
      open
      onClose={onClose}
      title="Which company is this lead from?"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {/* A disabled Button never receives the hover, so the reason sits on a wrapper. */}
          <span
            {...(canContinue ? {} : { title: 'Choose a company, or choose to create a new one.' })}
          >
            <Button
              variant="primary"
              disabled={!canContinue}
              onClick={() => {
                if (choice === CREATE_NEW) {
                  setStage({ name: 'creating' });
                  return;
                }
                const picked = lookup.matches.find((match) => String(match.id) === choice);
                if (!picked) return;
                setStage({
                  name: 'deal',
                  company: { kind: 'chosen', company: { id: picked.id, name: picked.name } },
                });
              }}
            >
              Continue
            </Button>
          </span>
        </div>
      }
    >
      {looking ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <CompanyChoices
          typedName={lead.organisation}
          matches={lookup.matches}
          lookupFailed={lookup.failed}
          noName={lookup.noName}
          choice={choice}
          onChoose={setChoice}
          namesLabel="The lead names"
          noNameSentence="The lead records no organisation, so nothing could be looked up."
          radioName="report-lead-convert-company"
        />
      )}
    </Modal>
  );
}

/**
 * What actually happened, step by step. ⛔ No step speaks for another, and
 * nothing claims a rollback — a company created before a refused deal still
 * exists.
 */
function ReportBody({ report }: { report: ConvertReport }) {
  const created = report.company.kind === 'created';
  const companyName = report.company.company.name;

  return (
    <div className="space-y-3 text-xs">
      <ReportLine
        tone={created ? 'success' : 'neutral'}
        headline={
          created
            ? `${companyName} was created.`
            : report.company.kind === 'linked'
              ? `${companyName} is the company linked to this lead. Nothing was changed on it.`
              : `${companyName} was the company chosen. Nothing was changed on it.`
        }
        notes={
          created
            ? [
                'It is awaiting approval. The approval round was raised with it; this screen cannot say who was emailed.',
              ]
            : []
        }
      />

      {report.deal.kind === 'created' ? (
        <>
          <ReportLine
            tone="success"
            headline={`Deal #${report.deal.deal.id} was created against ${companyName}.`}
            notes={[]}
          />
          {report.deal.linked === 'unconfirmed' && (
            <ReportLine
              tone="warning"
              headline="Whether the deal was recorded on this lead could not be confirmed."
              notes={[
                'The deal exists and is safe. Check whether the lead names this deal before converting it again — converting a second time may raise a duplicate.',
                ...(report.deal.linkMessage ? [report.deal.linkMessage] : []),
              ]}
            />
          )}
          {report.deal.linked === 'failed' && (
            <ReportLine
              tone="warning"
              headline="The deal was not recorded on this lead."
              notes={[
                'The deal exists and is safe. The lead still shows as unconverted, so it can be converted a second time until this is put right.',
                ...(report.deal.linkMessage ? [report.deal.linkMessage] : []),
              ]}
            />
          )}
        </>
      ) : (
        <ReportLine
          tone={created ? 'warning' : 'neutral'}
          headline="No deal was created."
          notes={
            created
              ? [
                  `${companyName} created, awaiting approval. No deal raised.`,
                ]
              : []
          }
        />
      )}
    </div>
  );
}
